import React, { useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  TableContainer,
  Grid,
  Stack,
  Chip,
  Divider,
  Alert,
  TablePagination,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Layout from "../../Layout";
import { useLocation, useNavigate } from "react-router-dom";
import { Edit, RestartAlt, FileDownload, PictureAsPdf, GridOn } from "@mui/icons-material";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import {
  DETAILED_REPORT_CATEGORIES,
  RISK_LEVELS,
  UNSUPPORTED_VALUE,
  canonicalRisk,
  deriveDetailedReportCategory,
  riskBucket,
} from "../../utils/trackerNormalization";
/* ChartJS */
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, ChartTooltip, Legend);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart, _args, opts) {
    if (!opts?.text) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.font = "600 18px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    ctx.fillStyle = "#333";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;
    ctx.fillText(opts.text, x, y);
    ctx.restore();
  },
};
ChartJS.register(centerTextPlugin);

/* ---------- Constants ---------- */
const riskLevels = RISK_LEVELS;
const allCategories = DETAILED_REPORT_CATEGORIES;
const overdueCategories = ["Open", "In-process", "Can Not Comply", "Not Complied"];

const riskColors = { High: "#e53935", Medium: "#fb8c00", Low: "#43a047", [UNSUPPORTED_VALUE]: "#757575" };
const statusFillColors = {
  "Pending for Review": "#90a4ae",
  "Pending for Discrepancy": "#ffb300",
  Open: "#1e88e5",
  "In-process": "#fb8c00",
  "Can Not Comply": "#e53935",
  "Not Complied": "#8e24aa",
  "Complied On Time": "#43a047",
  Delayed: "#ef6c00",
  "Delayed Compliance": "#f4511e",
  "Delayed Complied": "#d84315",
  "Not Applicable": "#9e9e9e",
  [UNSUPPORTED_VALUE]: "#616161",
};
const statusChipColor = {
  "Pending for Review": "default",
  "Pending for Discrepancy": "warning",
  Open: "info",
  "In-process": "warning",
  "Can Not Comply": "error",
  "Not Complied": "error",
  "Complied On Time": "success",
  Delayed: "warning",
  "Delayed Compliance": "warning",
  "Delayed Complied": "warning",
  "Not Applicable": "default",
  [UNSUPPORTED_VALUE]: "default",
};
const panelSx = { p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" };

/* ---------- Helpers ---------- */
const normalizeRisk = canonicalRisk;

const formatDateInIST = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return `${values.year}-${values.month}-${values.day}`;
};

const formatPeople = (people) =>
  Array.isArray(people) && people.length
    ? people
        .filter(Boolean)
        .map((person) =>
          [person.name, person.role ? `(${person.role})` : ""].filter(Boolean).join(" ")
        )
        .join("; ")
    : "";

const sanitizeSheetName = (name) =>
    String(name || "Sheet").replace(/[/\\?*[\]:]/g, " ").trim().slice(0, 31) || "Sheet";

const buildUniqueSheetNames = (names, reservedNames = []) => {
  const used = new Set(reservedNames.map((name) => String(name).toLowerCase()));

  return names.reduce((result, name) => {
    const base = sanitizeSheetName(name);
    let candidate = base;
    let suffixNumber = 2;

    while (used.has(candidate.toLowerCase())) {
      const suffix = ` (${suffixNumber})`;
      candidate = `${base.slice(0, 31 - suffix.length).trim()}${suffix}`;
      suffixNumber += 1;
    }

    used.add(candidate.toLowerCase());
    result[name] = candidate;
    return result;
  }, {});
};
  
  const mapTrackerToRow = (t, meta) => {
    const statusCat = deriveDetailedReportCategory(t);
    const risk = normalizeRisk(t?.risk);
    const comp = getCompletionDate(t);
    return {
      UIN: t?.uniqueIdentifier || "",
      Company: meta.company || "",
      Location: t?.location || "",
      "Compliance Activity": t?.complianceActivity || "",
      "Law Area": t?.lawArea || "",
      Description: t?.description || "",
      Category: Array.isArray(t?.category) ? t.category.join(", ") : t?.category || "",
      "Act or Rule": t?.actOrRule || "",
      Periodicity: t?.periodicity || "",
      Consequence: t?.consequence || "",
      "Source URL": t?.sourceURL || "",
      "Due Date (IST)": formatDateInIST(t?.dueDate),
      "Completion Date (IST)": formatDateInIST(comp),
      Risk: risk,
      Status: statusCat,
      "Raw Risk": t?.risk || "",
      "Raw Status": t?.status || "",
      "Proof Submitted": t?.proof ? "Yes" : "No",
      "Proof Not Required": t?.proofNotRequired ? "Yes" : "No",
      "Tracker Owner(s)": formatPeople(t?.trackerOwner),
      "Tracker Manager(s)": formatPeople(t?.trackerManager),
      "Legal Update Link": t?.legalUpdateLink || "",
    };
  };
  
  const sortByDueDateAsc = (arr) =>
    arr.slice().sort((a, b) => {
      const da = a?.dueDate ? new Date(a.dueDate) : null;
      const db = b?.dueDate ? new Date(b.dueDate) : null;
      if (da && db) return da - db;
      if (da && !db) return -1; // with due date first
      if (!da && db) return 1;  // no due date last
      // tie-breaker: UIN
      return String(a?.uniqueIdentifier || "").localeCompare(String(b?.uniqueIdentifier || ""));
    });
  
  /** Build grouped + sorted view based on (filtered ? detailsRows : allTrackers) */
  const buildGroupedSorted = (useFiltered, allTrackers, detailsRows) => {
    const src = useFiltered && detailsRows.length ? detailsRows : allTrackers;
    const groups = {};
    src.forEach((t) => {
      const la = t?.lawArea || "Unspecified";
      (groups[la] ||= []).push(t);
    });
    const orderedLawAreas = Object.keys(groups).sort((a, b) => a.localeCompare(b));
    orderedLawAreas.forEach((la) => {
      groups[la] = sortByDueDateAsc(groups[la]);
    });
    return { groups, orderedLawAreas };
  };
  

const parseDateSafe = (d) => {
  const dt = new Date(d);
  return isNaN(dt) ? null : dt;
};
const getCompletionDate = (t) =>
  parseDateSafe(t?.completionDate || t?.completedAt || t?.updatedAt);

const deriveCategory = deriveDetailedReportCategory;

/* ===================== PAGE ===================== */
const DetailedReportResults = () => {
    const statusChartRef = useRef(null);
  const riskShareChartRef = useRef(null);
  const topHighChartRef = useRef(null);

  const chartToPng = (ref) => {
    try {
      const chart = ref?.current;
      return chart?.toBase64Image?.("image/png", 1.0) || null;
    } catch {
      return null;
    }
  };
  const navigate = useNavigate();
  const { state } = useLocation(); // { reportData, Company, Location, startDate?, enddate? }
  const meta = {
    company: state?.Company || "",
    locations: Array.isArray(state?.Location) ? state?.Location.join(", ") : state?.Location || "",
    startDate: state?.startDate || state?.startdate || "",
    endDate: state?.endDate || state?.enddate || "",
  };

  const grouped = state?.reportData?.groupedTrackers || state?.reportData || {};
  const [error] = useState("");

  const [selectedCell, setSelectedCell] = useState({ lawArea: null, status: null, risk: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Export menu anchor
  const [exportAnchor, setExportAnchor] = useState(null);
  const openExportMenu = (e) => setExportAnchor(e.currentTarget);
  const closeExportMenu = () => setExportAnchor(null);

  const hasInput = grouped && typeof grouped === "object" && Object.keys(grouped).length > 0;

  /* Normalize arrays per lawArea key (BSE, CDSL, NSE, etc.) */
  const lawAreaMap = useMemo(() => {
    if (!hasInput) return {};
    const map = {};
    Object.entries(grouped).forEach(([lawArea, arr]) => {
      map[lawArea] = (Array.isArray(arr) ? arr : [arr]).filter(Boolean);
    });
    return map;
  }, [grouped, hasInput]);

  const allTrackers = useMemo(() => Object.values(lawAreaMap).flat(), [lawAreaMap]);

  /* Aggregate: counts per lawArea -> category -> risk */
  const processed = useMemo(() => {
    const proc = {};
    Object.entries(lawAreaMap).forEach(([lawArea, items]) => {
      const init = () => ({
        High: 0,
        Medium: 0,
        Low: 0,
        [UNSUPPORTED_VALUE]: 0,
      });
      proc[lawArea] = {};
      allCategories.forEach((c) => (proc[lawArea][c] = init()));
      items.forEach((t) => {
        const cat = deriveCategory(t);
        const risk = riskBucket(t?.risk);
        proc[lawArea][cat][risk] += 1;
      });
    });
    return proc;
  }, [lawAreaMap]);

  /* KPIs */
  const kpis = useMemo(() => {
    let total = 0;
    let overdueTotal = 0;
    let highOverdue = 0;
    let delayed = 0;
    let ontime = 0;
    let pending = 0;

    Object.keys(processed).forEach((la) => {
      allCategories.forEach((cat) => {
        riskLevels.forEach((r) => (total += processed[la]?.[cat]?.[r] || 0));
      });
      overdueCategories.forEach((cat) => {
        riskLevels.forEach((r) => {
          const v = processed[la]?.[cat]?.[r] || 0;
          overdueTotal += v;
          if (r === "High") highOverdue += v;
        });
      });
      delayed += ["Delayed", "Delayed Compliance", "Delayed Complied"].reduce(
        (sum, category) => sum + riskLevels.reduce((s, r) => s + (processed[la]?.[category]?.[r] || 0), 0),
        0
      );
      ontime += riskLevels.reduce((s, r) => s + (processed[la]?.["Complied On Time"]?.[r] || 0), 0);
      pending += ["Pending for Review", "Pending for Discrepancy"].reduce(
        (s, cat) => s + riskLevels.reduce((a, r) => a + (processed[la]?.[cat]?.[r] || 0), 0),
        0
      );
    });

    const lawAreasAffected = Object.keys(processed).filter((la) =>
      allCategories.some((cat) => riskLevels.some((r) => (processed[la]?.[cat]?.[r] || 0) > 0))
    ).length;

    const highPctOverdue = overdueTotal ? Math.round((highOverdue / overdueTotal) * 100) : 0;

    return { total, overdueTotal, highOverdue, highPctOverdue, delayed, ontime, pending, lawAreasAffected };
  }, [processed]);

  /* Charts */
  const statusDonutData = useMemo(() => {
    const labels = allCategories;
    const values = labels.map((cat) =>
      Object.keys(processed).reduce((sum, la) => {
        const bucket = processed[la]?.[cat] || {};
        return sum + riskLevels.reduce((a, r) => a + (bucket[r] || 0), 0);
      }, 0)
    );
    const filtered = labels.map((label, i) => ({ label, value: values[i] })).filter((d) => d.value > 0);

    return {
      labels: filtered.map((f) => f.label),
      datasets: [
        {
          label: "Items",
          data: filtered.map((f) => f.value),
          backgroundColor: filtered.map((f) => statusFillColors[f.label] || "#bdbdbd"),
          borderWidth: 0,
        },
      ],
    };
  }, [processed]);

  const riskShareByLaw = useMemo(() => {
    const labels = Object.keys(processed);
    const totalsByLaw = labels.map((la) =>
      allCategories.reduce(
        (sum, cat) => sum + riskLevels.reduce((acc, r) => acc + (processed[la]?.[cat]?.[r] || 0), 0),
        0
      )
    );
    return {
      labels,
      datasets: riskLevels.map((risk) => ({
        label: `${risk} Risk`,
        data: labels.map((la, i) => {
          const count = allCategories.reduce((s, cat) => s + (processed[la]?.[cat]?.[risk] || 0), 0);
          const total = totalsByLaw[i] || 1;
          return Math.round((count / total) * 100);
        }),
        backgroundColor: riskColors[risk],
        stack: "percent",
      })),
    };
  }, [processed]);

  const topHighRiskOverdue = useMemo(() => {
    const rows = Object.entries(processed).map(([la, by]) => {
      const hi = overdueCategories.reduce((s, cat) => s + (by?.[cat]?.High || 0), 0);
      return { lawArea: la, high: hi };
    });
    const top = rows.sort((a, b) => b.high - a.high).slice(0, 8);
    return {
      labels: top.map((t) => t.lawArea),
      datasets: [{ label: "High Risk (Overdue)", data: top.map((t) => t.high), backgroundColor: riskColors.High }],
    };
  }, [processed]);

  /* Interactions */
  const setSelectionFromLabel = useCallback((lawArea, status, risk) => {
    setSelectedCell({ lawArea: lawArea ?? null, status: status ?? null, risk: risk ?? null });
    setPage(0);
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedCell({ lawArea: null, status: null, risk: null });
    setPage(0);
  }, []);
  const handleCellClick = useCallback((lawArea, status, risk) => {
    setSelectedCell((prev) => {
      const same = prev.lawArea === lawArea && prev.status === status && prev.risk === risk;
      return same ? { lawArea: null, status: null, risk: null } : { lawArea, status, risk };
    });
    setPage(0);
  }, []);
  const isSelected = (lawArea, status, risk) =>
    selectedCell.lawArea === lawArea && selectedCell.status === status && selectedCell.risk === risk;

  /* Details */
  const detailsRows = useMemo(() => {
    if (!selectedCell.lawArea && !selectedCell.status && !selectedCell.risk) return [];
    const pool = selectedCell.lawArea ? (lawAreaMap[selectedCell.lawArea] || []) : Object.values(lawAreaMap).flat();

    return pool.filter((t) => {
      const statusCat = deriveCategory(t);
      const r = riskBucket(t?.risk);
      const statusMatch = selectedCell.status ? statusCat === selectedCell.status : true;
      const riskMatch = selectedCell.risk ? r === selectedCell.risk : true;
      const lawMatch = selectedCell.lawArea ? (t?.lawArea || "Unspecified") === selectedCell.lawArea : true;
      return statusMatch && riskMatch && lawMatch;
    });
  }, [lawAreaMap, selectedCell]);

  const buildSummaryMatrixRows = () => {
    const rows = [];
    Object.entries(processed).forEach(([lawArea, statuses]) => {
      allCategories.forEach((cat) => {
        const High = statuses?.[cat]?.High || 0;
        const Medium = statuses?.[cat]?.Medium || 0;
        const Low = statuses?.[cat]?.Low || 0;
        const Total = High + Medium + Low;
        if (Total > 0) rows.push({ "Law Area": lawArea, Status: cat, High, Medium, Low, Total });
      });
    });
    return rows;
  };

  const makeFilename = (ext, filtered) => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const scope = filtered ? "filtered" : "all";
    const company = meta.company ? meta.company.replace(/[^\w- ]/g, "").replace(/\s+/g, "_") : "company";
    return `compliance_report_${company}_${scope}_${ts}.${ext}`;
  };

  const exportXLSX = (filtered) => {
    const { groups, orderedLawAreas } = buildGroupedSorted(filtered, allTrackers, detailsRows);
    const wb = XLSX.utils.book_new();
    const mainSheetName = filtered ? "Filtered (Grouped)" : "All (Grouped)";
    const lawAreaSheetNames = buildUniqueSheetNames(orderedLawAreas, [
      mainSheetName,
      "Summary Matrix",
      "Meta",
    ]);
  
    // 1) Main sheet (grouped & sorted in one sheet)
    const flatRows = [];
    orderedLawAreas.forEach((la) => {
      groups[la].forEach((t) => flatRows.push(mapTrackerToRow(t, meta)));
    });
    const wsMain = XLSX.utils.json_to_sheet(flatRows);
    XLSX.utils.book_append_sheet(
      wb,
      wsMain,
      mainSheetName
    );
  
    // 2) One sheet per Law Area (nice for browsing by area)
    orderedLawAreas.forEach((la) => {
      const ws = XLSX.utils.json_to_sheet(groups[la].map((t) => mapTrackerToRow(t, meta)));
      XLSX.utils.book_append_sheet(wb, ws, lawAreaSheetNames[la]);
    });
  
    // 3) Summary Matrix (unchanged)
    const summary = [];
    Object.entries(processed).forEach(([lawArea, statuses]) => {
      allCategories.forEach((cat) => {
        const High = statuses?.[cat]?.High || 0;
        const Medium = statuses?.[cat]?.Medium || 0;
        const Low = statuses?.[cat]?.Low || 0;
        const Total = High + Medium + Low;
        if (Total > 0) summary.push({ "Law Area": lawArea, Status: cat, High, Medium, Low, Total });
      });
    });
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary Matrix");
  
    // 4) Meta/KPIs (unchanged)
    const metaSheet = XLSX.utils.aoa_to_sheet([
      ["Company", meta.company || "-"],
      ["Locations", meta.locations || "-"],
      ["Start Date", meta.startDate || "-"],
      ["End Date", meta.endDate || "-"],
      ["Generated", new Date().toLocaleString()],
      [],
      ["KPI", "Value"],
      ["Total Trackers", kpis.total],
      ["Total Overdue", kpis.overdueTotal],
      ["High-Risk Overdue", kpis.highOverdue],
      ["High-Risk % Overdue", `${kpis.highPctOverdue}%`],
      ["Complied On Time", kpis.ontime],
      ["Delayed Compliance", kpis.delayed],
      ["Pending (Review/Discrepancy)", kpis.pending],
    ]);
    XLSX.utils.book_append_sheet(wb, metaSheet, "Meta");
  
    XLSX.writeFile(wb, makeFilename("xlsx", filtered));
  };
  
const exportExcelWithCharts = async (filtered) => {
    const { groups, orderedLawAreas } = buildGroupedSorted(filtered, allTrackers, detailsRows);
    const wb = new ExcelJS.Workbook();
    wb.creator = "DetailedReportResults";
    wb.created = new Date();
    const mainSheetName = filtered ? "Filtered (Grouped)" : "All (Grouped)";
    const lawAreaSheetNames = buildUniqueSheetNames(orderedLawAreas, [
      mainSheetName,
      "Summary Matrix",
      "Meta",
      "Charts",
    ]);
  
    // 1) Main sheet (grouped)
    const main = wb.addWorksheet(mainSheetName);
    const flatRows = [];
    orderedLawAreas.forEach((la) => {
      groups[la].forEach((t) => flatRows.push(mapTrackerToRow(t, meta)));
    });
    if (flatRows.length) {
      main.addRow(Object.keys(flatRows[0]));
      flatRows.forEach((r) => main.addRow(Object.values(r)));
      main.columns.forEach((c) => { c.width = Math.min(40, Math.max(12, (c.header?.length || 12))); });
      main.getRow(1).font = { bold: true };
    }
  
    // 2) One sheet per Law Area
    orderedLawAreas.forEach((la) => {
      const ws = wb.addWorksheet(lawAreaSheetNames[la]);
      const rows = groups[la].map((t) => mapTrackerToRow(t, meta));
      if (rows.length) {
        ws.addRow(Object.keys(rows[0]));
        rows.forEach((r) => ws.addRow(Object.values(r)));
        ws.columns.forEach((c) => { c.width = Math.min(40, Math.max(12, (c.header?.length || 12))); });
        ws.getRow(1).font = { bold: true };
      }
    });
  
    // 3) Summary Matrix
    const wsSummary = wb.addWorksheet("Summary Matrix");
    const summary = buildSummaryMatrixRows();
    if (summary.length) {
      wsSummary.addRow(Object.keys(summary[0]));
      summary.forEach((r) => wsSummary.addRow(Object.values(r)));
      wsSummary.columns.forEach((c) => { c.width = Math.min(24, Math.max(10, (c.header?.length || 10))); });
      wsSummary.getRow(1).font = { bold: true };
    }
  
    // 4) Meta / KPIs
    const wsMeta = wb.addWorksheet("Meta");
    wsMeta.addRows([
      ["Company", meta.company || "-"],
      ["Locations", meta.locations || "-"],
      ["Start Date", meta.startDate || "-"],
      ["End Date", meta.endDate || "-"],
      ["Generated", new Date().toLocaleString()],
      [],
      ["KPI", "Value"],
      ["Total Trackers", kpis.total],
      ["Total Overdue", kpis.overdueTotal],
      ["High-Risk Overdue", kpis.highOverdue],
      ["High-Risk % Overdue", `${kpis.highPctOverdue}%`],
      ["Complied On Time", kpis.ontime],
      ["Delayed Compliance", kpis.delayed],
      ["Pending (Review/Discrepancy)", kpis.pending],
    ]);
    wsMeta.columns = [{ width: 26 }, { width: 32 }];
    wsMeta.getRow(7).font = { bold: true };
    const wsCharts = wb.addWorksheet("Charts");
    const imgStatus = chartToPng(statusChartRef);
    const imgRiskShare = chartToPng(riskShareChartRef);
    const imgTopHigh = chartToPng(topHighChartRef);
    const addImg = (base64, tlCol, tlRow, width, height) => {
      const id = wb.addImage({ base64, extension: "png" });
      wsCharts.addImage(id, { tl: { col: tlCol, row: tlRow }, ext: { width, height } });
    };

    const leftW = 800, rightW = 800, tallH = 360;
    if (imgStatus) addImg(imgStatus, 0, 0, leftW, tallH);
    if (imgRiskShare) addImg(imgRiskShare, 9, 0, rightW, tallH);
    if (imgTopHigh) addImg(imgTopHigh, 0, 18, leftW + rightW + 40, 380);
    wsCharts.getColumn(1).width = 12;
  
    // Download
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = makeFilename("xlsx", filtered).replace(".xlsx", "_with_charts.xlsx");
    a.click();
    URL.revokeObjectURL(a.href);
  };


const exportPDF = (filtered) => {
  const { groups, orderedLawAreas } = buildGroupedSorted(filtered, allTrackers, detailsRows);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  // Header
  doc.setFontSize(16);
  doc.text("Detailed Compliance Report", 40, 40);
  doc.setFontSize(10);
  const metaLines = [
    `Company: ${meta.company || "-"}`,
    `Locations: ${meta.locations || "-"}`,
    `Range: ${meta.startDate || "-"} to ${meta.endDate || "-"}`,
    `Generated: ${new Date().toLocaleString()}`,
    `Scope: ${filtered ? "Filtered selection" : "All trackers"}`,
  ];
  metaLines.forEach((ln, i) => doc.text(ln, 40, 60 + i * 14));


  // --- Charts section ---
  const chartsY = 60 + metaLines.length * 14 + 16;
  const pageW = doc.internal.pageSize.getWidth();
  const left = 40;
  const gap = 24;
  const colW = (pageW - left * 2 - gap) / 2;  // two columns
  const imgH = 220; // consistent height

  const imgStatus = chartToPng(statusChartRef);
  const imgRiskShare = chartToPng(riskShareChartRef);
  const imgTopHigh = chartToPng(topHighChartRef);

  doc.setFontSize(12);
  doc.text("Charts", left, chartsY);

  let y = chartsY + 14;
  if (imgStatus) {
    doc.addImage(imgStatus, "PNG", left, y, colW, imgH);
  }
  if (imgRiskShare) {
    doc.addImage(imgRiskShare, "PNG", left + colW + gap, y, colW, imgH);
 }
  y += imgH + 18;
  if (imgTopHigh) {
    // full width for the horizontal bar
    doc.addImage(imgTopHigh, "PNG", left, y, pageW - left * 2, imgH);
    y += imgH;
 }
 doc.addPage();


  const columns = [
    "UIN",
    "Location",
    "Activity",
    "Tracker Details",
    "Dates (IST)",
    "Status / Risk",
    "Owners / Managers",
    "Legal Update Link",
  ];

  let startY = 40;

  orderedLawAreas.forEach((la, idx) => {
    // If we are too close to bottom, start a new page with a small top margin
    if (idx > 0 && startY > doc.internal.pageSize.getHeight() - 120) {
      doc.addPage();
      startY = 40;
    }

    // Section title
    doc.setFontSize(12);
    doc.text(`Law Area: ${la}`, 40, startY);
    startY += 8;

    const rows = groups[la].map((t) => mapTrackerToRow(t, meta));

    autoTable(doc, {
      startY,
      head: [columns],
      body: rows.map((row) => [
        row.UIN,
        row.Location,
        row["Compliance Activity"],
        [
          `Description: ${row.Description || "-"}`,
          `Category: ${row.Category || "-"}`,
          `Act/Rule: ${row["Act or Rule"] || "-"}`,
          `Periodicity: ${row.Periodicity || "-"}`,
          `Consequence: ${row.Consequence || "-"}`,
          `Source: ${row["Source URL"] || "-"}`,
        ].join("\n"),
        [
          `Due: ${row["Due Date (IST)"] || "-"}`,
          `Completed: ${row["Completion Date (IST)"] || "-"}`,
        ].join("\n"),
        [
          `Status: ${row.Status || "-"}`,
          `Risk: ${row.Risk || "-"}`,
          `Proof submitted: ${row["Proof Submitted"]}`,
          `Proof not required: ${row["Proof Not Required"]}`,
        ].join("\n"),
        [
          `Owners: ${row["Tracker Owner(s)"] || "-"}`,
          `Managers: ${row["Tracker Manager(s)"] || "-"}`,
        ].join("\n"),
        row["Legal Update Link"],
      ]),
      styles: { fontSize: 5.5, cellPadding: 2, overflow: "linebreak", valign: "top" },
      headStyles: { fillColor: [30, 136, 229], fontSize: 6, halign: "center" },
      columnStyles: {
        0: { cellWidth: 42 },
        1: { cellWidth: 44 },
        2: { cellWidth: 68 },
        3: { cellWidth: 170 },
        4: { cellWidth: 68 },
        5: { cellWidth: 74 },
        6: { cellWidth: 100 },
        7: { cellWidth: 145 },
      },
      didDrawPage: (data) => {
        // Footer with page number
        const str = `Page ${doc.internal.getNumberOfPages()}`;
        doc.setFontSize(9);
        doc.text(str, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
      },
    });

   
    startY = (doc.lastAutoTable?.finalY || startY) + 16;
  });

  doc.save(makeFilename("pdf", filtered));
};

  
  if (!hasInput) {
    return (
      <Layout title="Detailed Report">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography variant="h6" align="center" color="text.secondary">
            No data to display.
          </Typography>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Detailed Report">
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Detailed Compliance Report
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label="High" size="small" sx={{ bgcolor: "#fdecea", color: "#b71c1c" }} />
            <Chip label="Medium" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100" }} />
            <Chip label="Low" size="small" sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }} />

            {/* Export */}
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={openExportMenu}
              sx={{ ml: 1 }}
            >
              Export
            </Button>
            <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={closeExportMenu}>
              <MenuItem
                onClick={() => {
                  exportXLSX(false);
                  closeExportMenu();
                }}
              >
                <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                <ListItemText>Excel (.xlsx) — All trackers</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  exportXLSX(true);
                  closeExportMenu();
                }}
              >
                <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                <ListItemText>Excel (.xlsx) — Filtered (Details)</ListItemText>
              </MenuItem>
              +  <MenuItem
    onClick={async () => {
      await exportExcelWithCharts(false);
      closeExportMenu();
    }}
  >
    <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
    <ListItemText>Excel (.xlsx) — All + Charts</ListItemText>
  </MenuItem>
  <MenuItem
    onClick={async () => {
      await exportExcelWithCharts(true);
      closeExportMenu();
    }}
  >
    <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
    <ListItemText>Excel (.xlsx) — Filtered + Charts</ListItemText>
  </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  exportPDF(false);
                  closeExportMenu();
                }}
              >
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                <ListItemText>PDF — All trackers (incl. charts)</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  exportPDF(true);
                  closeExportMenu();
                }}
              >
                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                <ListItemText>PDF — Filtered (incl. charts)</ListItemText>
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* KPI STRIP */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[
            { label: "Total Trackers", value: kpis.total },
            { label: "Total Overdue (Open/In-process/Cannot/Not Complied)", value: kpis.overdueTotal },
            { label: "High-Risk (Overdue)", value: kpis.highOverdue },
            { label: "High-Risk % of Overdue", value: `${kpis.highPctOverdue}%` },
          ].map((k, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={panelSx}>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {k.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {k.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Complied & Pending KPIs */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[
            { label: "Complied On Time", value: kpis.ontime },
            { label: "Delayed Compliance", value: kpis.delayed },
            { label: "Pending (Review/Discrepancy)", value: kpis.pending },
            { label: "Law Areas with Any Trackers", value: kpis.lawAreasAffected },
          ].map((k, i) => (
            <Grid key={i} item xs={12} sm={6} md={3}>
              <Paper variant="outlined" sx={panelSx}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                  {k.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {k.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Charts */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={5} lg={4}>
            <Paper variant="outlined" sx={{ ...panelSx, height: 420 }}>
              <Typography variant="h6" gutterBottom>
                Status Distribution (All)
              </Typography>
              <Box sx={{ position: "relative", height: 330 }}>
                <Doughnut
                ref={statusChartRef}
                  data={statusDonutData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                      title: { display: true, text: "Trackers by Status" },
                      centerText: {
                        text: String((statusDonutData?.datasets?.[0]?.data || []).reduce((a, b) => a + b, 0)),
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const value = ctx.raw ?? 0;
                            const total = (ctx.dataset.data || []).reduce((a, b) => a + b, 0) || 1;
                            const pct = Math.round((value / total) * 100);
                            return `${ctx.label}: ${value} (${pct}%)`;
                          },
                        },
                      },
                    },
                    cutout: "65%",
                  }}
                  onClick={(_e, els) => {
                    if (!els?.length) return;
                    const { index } = els[0];
                    const status = statusDonutData.labels[index];
                    setSelectionFromLabel(null, status, null);
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7} lg={8}>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ ...panelSx, height: 280 }}>
                <Typography variant="h6" gutterBottom>
                  Risk Share by Law Area (All Statuses)
                </Typography>
                <Box sx={{ position: "relative", height: 200 }}>
                  <Bar
                  ref={riskShareChartRef}
                    data={riskShareByLaw}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                        title: { display: true, text: "Risk Composition (100%)" },
                        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%` } },
                      },
                      scales: {
                        x: { stacked: true },
                        y: { stacked: true, beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
                      },
                    }}
                    onClick={(_e, els) => {
                      if (!els?.length) return;
                      const { datasetIndex, index } = els[0];
                      const lawArea = riskShareByLaw.labels[index];
                      const risk = (riskShareByLaw.datasets[datasetIndex].label || "").replace(" Risk", "");
                      setSelectionFromLabel(lawArea, null, risk);
                    }}
                  />
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ ...panelSx, height: 320 }}>
                <Typography variant="h6" gutterBottom>
                  Top High-Risk Law Areas (Overdue)
                </Typography>
                <Box sx={{ position: "relative", height: 240 }}>
                  <Bar
                  ref={topHighChartRef}
                    data={topHighRiskOverdue}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: "y",
                      plugins: {
                        legend: { display: false },
                        title: { display: true, text: "High Risk Items (Top 8)" },
                      },
                      scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
                    }}
                    onClick={(_e, els) => {
                      if (!els?.length) return;
                      const { index } = els[0];
                      const lawArea = topHighRiskOverdue.labels[index];
                      setSelectionFromLabel(lawArea, null, "High");
                    }}
                  />
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {/* MATRIX: all statuses */}
        <Paper variant="outlined" sx={{ ...panelSx, mt: 3, overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            All Statuses Matrix
          </Typography>
          <TableContainer sx={{ maxHeight: { xs: 360, md: "50vh" } }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      minWidth: 200,
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      bgcolor: "background.paper",
                      borderRight: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    Law Area
                  </TableCell>
                  {riskLevels.map((risk) => (
                    <TableCell
                      key={`risk-${risk}`}
                      align="center"
                      colSpan={allCategories.length}
                      sx={{ fontWeight: 700, borderLeft: "1px solid", borderColor: "divider" }}
                    >
                      {risk}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {riskLevels.map((risk) =>
                    allCategories.map((category) => (
                      <TableCell key={`hdr-${risk}-${category}`} align="center" sx={{ fontWeight: 600, minWidth: 160 }}>
                        {category}
                      </TableCell>
                    ))
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(processed).map((lawArea) => (
                  <TableRow hover key={lawArea}>
                    <TableCell
                      align="left"
                      sx={{
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        bgcolor: "background.paper",
                        borderRight: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {lawArea}
                    </TableCell>

                    {riskLevels.map((risk) =>
                      allCategories.map((category) => {
                        const count = processed[lawArea]?.[category]?.[risk] || 0;
                        const selected = isSelected(lawArea, category, risk);
                        const alpha = Math.min(count / 10, 0.25);
                        const bg =
                          risk === "High"
                            ? `rgba(229,57,53,${alpha})`
                            : risk === "Medium"
                            ? `rgba(251,140,0,${alpha})`
                            : `rgba(67,160,71,${alpha})`;
                        return (
                          <Tooltip
                            key={`${lawArea}-${risk}-${category}`}
                            title={`${lawArea} • ${risk} • ${category} — ${count}`}
                            arrow
                          >
                            <TableCell
                              align="center"
                              onClick={() => handleCellClick(lawArea, category, risk)}
                              sx={(theme) => ({
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                bgcolor: selected ? theme.palette.action.selected : bg,
                                "&:hover": { bgcolor: theme.palette.action.hover },
                                borderLeft: "1px solid",
                                borderColor: "divider",
                                fontWeight: count > 0 ? 600 : 400,
                                color: count > 0 ? "text.primary" : "text.secondary",
                              })}
                            >
                              {count}
                            </TableCell>
                          </Tooltip>
                        );
                      })
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* DETAILS */}
        {(selectedCell.lawArea || selectedCell.status || selectedCell.risk) && (
          <Paper variant="outlined" sx={{ ...panelSx, mt: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6">Details</Typography>
                {selectedCell.lawArea && (
                  <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                    — {selectedCell.lawArea}
                  </Typography>
                )}
                {selectedCell.status && (
                  <Chip label={selectedCell.status} size="small" color={statusChipColor[selectedCell.status] || "default"} />
                )}
                {selectedCell.risk && (
                  <Chip
                    label={selectedCell.risk}
                    size="small"
                    sx={{
                      bgcolor: selectedCell.risk === "High" ? "#fdecea" : selectedCell.risk === "Medium" ? "#fff3e0" : "#e8f5e9",
                    }}
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {detailsRows.length} item{detailsRows.length === 1 ? "" : "s"}
                </Typography>
                <Button size="small" startIcon={<RestartAlt />} onClick={clearSelection}>
                  Reset filters
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>UIN</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Compliance Activity</TableCell>
                    <TableCell>Law Area</TableCell>
                    <TableCell>Act or Rule</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Updated At</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((t, idx) => {
                    const risk = normalizeRisk(t?.risk);
                    const statusCat = deriveCategory(t);
                    return (
                      <TableRow hover key={`${t?.uniqueIdentifier || "row"}-${idx}`} sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.uniqueIdentifier || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.location || "-"}</TableCell>
                        <TableCell>{t?.complianceActivity || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.lawArea || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.actOrRule || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.dueDate ? new Date(t.dueDate).toLocaleDateString() : "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : "-"}</TableCell>
                        <TableCell>
                          <Chip label={risk} size="small"
                            sx={{ bgcolor: risk === "High" ? "#fdecea" : risk === "Medium" ? "#fff3e0" : "#e8f5e9" }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={statusCat || "-"} size="small" color={statusChipColor[statusCat || ""] || "default"} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open details">
                            <IconButton size="small" onClick={() => navigate(`/Edit/${encodeURIComponent(t?.uniqueIdentifier || "")}`)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={detailsRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}
      </Container>
    </Layout>
  );
};

export default DetailedReportResults;
